import { createContext, useContext, useEffect, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'routemize-theme';

/* Light is the only theme the product ships.
 *
 * The dark token set exists in index.css and `[data-theme="dark"]` works, but
 * thousands of raw Tailwind palette classes (`bg-white`, `bg-blue-600`, …)
 * across the app ignore the tokens entirely, so a dark document renders as a
 * half-inverted, unreadable mix — scrims built on `bg-ink-950/60` invert into a
 * white haze over the page instead of dimming it. There is deliberately no UI
 * to reach dark mode until that migration is finished; re-enabling it means
 * restoring a setter here plus a control that calls it, not just flipping an
 * attribute.
 */
const THEME: Theme = 'light';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', THEME);
    // Clear the preference the old dark-mode toggle used to persist. Without
    // this, anyone who tried it stays stranded in the broken theme through
    // every reload with no way out but clearing site data by hand.
    window.localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: THEME }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
